import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../utils/DateUtils';
import { getIcon } from '../../../utils/IconHandler';
import B2BUtilsService from '../../../services/B2BUtilsService';
import dayjs from 'dayjs';
import { formatMoney } from '../../../utils/currency';

const ViewB2BOrderModal = ({ open, onClose, order }) => {
  const { t } = useTranslation();

  if (!order) return null;

  const formatCurrency = (amount) => {
    return formatMoney(amount);
  };

  const totalAmount = order.items?.reduce((total, item) => total + (item.totalPrice || 0), 0) || 0;
  const dueDate = order.dueDate || B2BUtilsService.calculateDueDate(order.orderDate, order.creditDays);
  const hasPendingBalance = order.paymentStatus !== 'PAID' && !!dueDate;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {getIcon('eye')}
        {t('order_details')} - ID: {order.id}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Información General */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                {t('order_information')}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('order_id')}:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {order.id}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('client')}:
                </Typography>
                <Typography variant="body1">
                  {order.clientName || t('unassigned')}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('order_date')}:
                </Typography>
                <Typography variant="body1">
                  {formatDate(dayjs(order.orderDate))}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('payment_status')}:
                </Typography>
                <Chip
                  label={B2BUtilsService.getBillingOrderStatusText(order.paymentStatus, dueDate, t)}
                  color={B2BUtilsService.getBillingOrderStatusColor(order.paymentStatus, dueDate)}
                  size="small"
                />
              </Box>

              {hasPendingBalance && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('due_date')}:
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(dayjs(dueDate))}
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('delivery_date')}:
                </Typography>
                <Typography variant="body1">
                  {order.deliveryDate ? formatDate(dayjs(order.deliveryDate)) : t('not_specified')}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  {t('total_amount')}:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(totalAmount)}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Items del Pedido */}
          <Typography variant="h6" gutterBottom>
            {t('order_items')}
          </Typography>
          
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('product')}</TableCell>
                  <TableCell align="center">Solicitado</TableCell>
                  <TableCell align="center">Aceptado</TableCell>
                  <TableCell align="center">{t('unit_price')}</TableCell>
                  <TableCell align="center">{t('subtotal')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {order.items?.map((item, index) => {
                  console.log('🔍 ViewB2BOrderModal - Item:', item);
                  const finalUnitPrice = item.quantity > 0 ? item.totalPrice / item.quantity : item.unitPrice;
                  
                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {item.productName}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{item.requestedQuantity ?? item.quantity}</TableCell>
                      <TableCell align="center">
                        <Typography
                          variant="body2"
                          color={(item.requestedQuantity ?? item.quantity) !== item.quantity ? 'primary.main' : 'text.primary'}
                          sx={{ fontWeight: (item.requestedQuantity ?? item.quantity) !== item.quantity ? 700 : 400 }}
                        >
                          {item.quantity}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {formatCurrency(finalUnitPrice)}
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {formatCurrency(item.totalPrice)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Total */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {t('total')}: {formatCurrency(totalAmount)}
              </Typography>
            </Box>
          </Box>

          {/* Detalle de Pagos */}
          {order.payments && order.payments.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                {t('payment_details')}
              </Typography>
              
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'success.light', borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ color: 'black', fontWeight: 'bold' }}>
                        {formatCurrency(order.paidAmount || 0)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'black' }}>
                        {t('paid_amount')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'error.light', borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ color: 'black', fontWeight: 'bold' }}>
                        {formatCurrency(order.pendingAmount || 0)}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'black' }}>
                        {t('pending_amount')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'info.light', borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ color: 'black', fontWeight: 'bold' }}>
                        {order.paymentsCount || 0}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'black' }}>
                        {t('payments_count')}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('payment_date')}</TableCell>
                      <TableCell align="center">{t('amount')}</TableCell>
                      <TableCell align="center">{t('method')}</TableCell>
                      <TableCell>{t('notes')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.payments.map((payment, index) => (
                      <TableRow key={payment.id || index}>
                        <TableCell>
                          {formatDate(dayjs(payment.paymentDate))}
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(payment.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={payment.method}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {payment.notes || t('no_notes')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Notas */}
          {order.notes && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom>
                {t('notes')}
              </Typography>
              <Typography variant="body2" sx={{ 
                backgroundColor: 'grey.50', 
                padding: 2, 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}>
                {order.notes}
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          {t('close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewB2BOrderModal;
